import {z as zod} from "zod";
import {
    type AmaronDispositionKanbanViewLevel,
    resolveAmaronDispositionIdsForKanbanColumnSlug,
} from "~/constants/amaronDispositionMapping";
import {AMARON_KANBAN_COLUMN_FIELD} from "~/constants/amaronDispositionMapping";
import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {SortDirection, type SortConfiguration} from "~/components/ottopilotDataViewer/sortConfiguration";
import {ottopilotEventContainerWithEventsValidator} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {
    amaronJourneyAonConfig,
    getAmaronDispositionIdsForLevels,
} from "~/domainModels/workspaceSpecific/amaron";
import {
    hasAmaronDispositionUiLevelFilters,
    isAmaronJourneyDispositionUiFilterColumn,
    readAmaronDispositionLevelsFromFilters,
} from "~/domainModels/workspaceSpecific/amaronDispositionFilters";
import {
    isAonFilterRangesLineItem,
    isAonUiOnlyFilterLineItem,
} from "~/utilities/syntheticFields/aon";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {
    integerZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    convertFilterConfigurationToSrimanFilters,
    convertSortConfigurationToSrimanOrderBy,
} from "~/utilities/apis/api";
import {normalizeColumnValuesFromApi} from "~/utilities/columnValuesFromApi";
import type {AmaronDispositionKanbanJourney} from "~/utilities/generateAmaronDispositionKanbanData";

export const AMARON_DISPOSITION_TRACKER_QUERY_API_PATH =
    "/api/workspace-specific/amaron/disposition-tracker/query";
export const AGENT_VIEW_DISPOSITION_TRACKER_QUERY_API_PATH =
    "/agent-view/home/disposition-tracker/query";

const amaronDispositionTrackerClientFilterLineItemSchema = zod.object({
    columnId: zod.string(),
    operation: zod.nativeEnum(FilterOperation),
    value: zod.unknown().optional(),
    values: zod.array(zod.string()).optional(),
});

const amaronDispositionTrackerSortLineItemSchema = zod.object({
    columnId: zod.string(),
    direction: zod.nativeEnum(SortDirection),
});

export const amaronDispositionTrackerQueryRequestSchema = zod.object({
    workspaceId: uuidZodValidator,
    viewLevel: zod.union([zod.literal(1), zod.literal(2), zod.literal(3), zod.literal(4)]),
    offset: zod.number().int().min(0),
    limit: zod.number().int().min(0),
    filters: zod.unknown(),
    orderBy: zod.array(
        zod.object({
            column: zod.string(),
            direction: zod.string(),
        }),
    ),
    notificationFilter: zod.string().optional(),
    kanbanColumnSlug: zod.string().nullable().optional(),
    dispositionFilterDispositionIds: zod.array(uuidZodValidator).optional(),
    clientFilterLineItems: zod.array(amaronDispositionTrackerClientFilterLineItemSchema).optional(),
    sortLineItems: zod.array(amaronDispositionTrackerSortLineItemSchema).optional(),
    facetColumnId: zod.string().optional(),
    includeRows: zod.boolean().optional(),
    includeTotalCount: zod.boolean().optional(),
    includeKanbanColumnCounts: zod.boolean().optional(),
});

export type AmaronDispositionTrackerQueryRequest = zod.infer<typeof amaronDispositionTrackerQueryRequestSchema>;

export type AgentViewDispositionTrackerQueryRequest = {
    includeKanbanColumnCounts?: boolean;
    includeRows?: boolean;
    includeTotalCount?: boolean;
    kanbanColumnSlug?: string | null;
    limit: number;
    offset: number;
    viewLevel: 1 | 2 | 3 | 4;
    workspaceId: Uuid;
};

const eventContainerWithEventsApiRowSchema = ottopilotEventContainerWithEventsValidator.extend({
    amaronKanbanColumnId: zod.string(),
    amaronQueueRank: zod.number(),
});

export const amaronDispositionTrackerQueryResponseSchema = zod.object({
    rows: zod.array(eventContainerWithEventsApiRowSchema),
    totalCount: integerZodValidator,
    kanbanColumnCounts: zod.record(zod.string(), integerZodValidator),
    columnValues: zod.array(zod.string()).nullable().optional(),
});

export type AmaronDispositionTrackerQueryResponse = zod.infer<typeof amaronDispositionTrackerQueryResponseSchema>;

export type AmaronDispositionTrackerQueryResult = {
    rows: Array<AmaronDispositionKanbanJourney>;
    totalCount: number;
    kanbanColumnCounts: Record<string, number>;
    columnValues?: Array<string>;
};

function mapApiEventContainerRowToKanbanEventContainer(
    row: zod.infer<typeof eventContainerWithEventsApiRowSchema>,
): AmaronDispositionKanbanJourney {
    const {amaronKanbanColumnId, amaronQueueRank, ...eventContainer} = row;
    return {
        ...eventContainer,
        [AMARON_KANBAN_COLUMN_FIELD]: amaronKanbanColumnId,
        amaronQueueRank,
    };
}

export function buildAmaronDispositionTrackerBackendFilters(args: {
    filterConfiguration: FilterConfiguration;
    searchConfiguration: SearchConfiguration;
    leadViewFilterLineItems: FilterConfiguration["lineItems"];
    mapFilterConfigurationToFields: (filterConfiguration: FilterConfiguration) => FilterConfiguration;
}): FilterConfiguration {
    const backendLineItems = [
        ...args.leadViewFilterLineItems,
        ...args.filterConfiguration.lineItems.filter(
            (lineItem) =>
                lineItem.columnId !== AMARON_KANBAN_COLUMN_FIELD &&
                isAmaronJourneyDispositionUiFilterColumn(lineItem.columnId) !== true &&
                args.leadViewFilterLineItems.some(
                    (leadLineItem) =>
                        leadLineItem.columnId === lineItem.columnId &&
                        leadLineItem.operation === lineItem.operation,
                ) !== true,
        ),
    ];

    return args.mapFilterConfigurationToFields({
        ...args.filterConfiguration,
        lineItems: backendLineItems,
    });
}

function shouldSendClientFilterLineItem(columnId: string): boolean {
    if (columnId === AMARON_KANBAN_COLUMN_FIELD) {
        return false;
    }
    if (isAmaronJourneyDispositionUiFilterColumn(columnId) === true) {
        return false;
    }
    if (columnId === "disposition") {
        return false;
    }
    return true;
}

function isClientOnlyFilterLineItem(
    lineItem: FilterConfiguration["lineItems"][number],
): boolean {
    if (shouldSendClientFilterLineItem(lineItem.columnId) !== true) {
        return false;
    }
    if (isAonFilterRangesLineItem(amaronJourneyAonConfig, lineItem) === true) {
        return false;
    }
    if (isAonUiOnlyFilterLineItem(amaronJourneyAonConfig, lineItem) === true) {
        return false;
    }
    return true;
}

export function buildAmaronDispositionTrackerClientFilterLineItems(
    filterConfiguration: FilterConfiguration,
): Array<zod.infer<typeof amaronDispositionTrackerClientFilterLineItemSchema>> {
    return filterConfiguration.lineItems
        .filter((lineItem) => isClientOnlyFilterLineItem(lineItem) === true)
        .map((lineItem) => ({
            columnId: lineItem.columnId,
            operation: lineItem.operation,
            value: "value" in lineItem ? lineItem.value : undefined,
            values: "values" in lineItem ? lineItem.values : undefined,
        }));
}

export function buildAmaronDispositionTrackerQueryRequest(args: {
    workspaceId: Uuid;
    viewLevel: AmaronDispositionKanbanViewLevel;
    offset: number;
    limit: number;
    filterConfiguration: FilterConfiguration;
    sortConfiguration: SortConfiguration;
    searchConfiguration: SearchConfiguration;
    leadViewFilterLineItems: FilterConfiguration["lineItems"];
    mapFilterConfigurationToFields: (filterConfiguration: FilterConfiguration) => FilterConfiguration;
    mapSortConfigurationToFields: (sortConfiguration: SortConfiguration) => SortConfiguration;
    notificationFilter?: string;
    kanbanColumnSlug?: string | null;
    /** When set without `kanbanColumnSlug`, still applies disposition ids for this bucket (row-fetch fallback). */
    kanbanColumnSlugForDispositionFilter?: string | null;
    facetColumnId?: string;
    includeRows?: boolean;
    includeTotalCount?: boolean;
    includeKanbanColumnCounts?: boolean;
}): AmaronDispositionTrackerQueryRequest {
    const backendFilterConfiguration = buildAmaronDispositionTrackerBackendFilters({
        filterConfiguration: args.filterConfiguration,
        searchConfiguration: args.searchConfiguration,
        leadViewFilterLineItems: args.leadViewFilterLineItems,
        mapFilterConfigurationToFields: args.mapFilterConfigurationToFields,
    });

    const activeLevels = readAmaronDispositionLevelsFromFilters(args.filterConfiguration.lineItems, "journey");
    const toolbarDispositionFilterIds = hasAmaronDispositionUiLevelFilters(activeLevels)
        ? getAmaronDispositionIdsForLevels(activeLevels)
        : [];
    // Per-column count + data: `kanbanColumnSlugForDispositionFilter` → disposition UUIDs at `viewLevel`.
    const slugForDispositionFilter = (args.kanbanColumnSlugForDispositionFilter ?? "").trim();
    const columnDispositionFilterIds =
        slugForDispositionFilter !== ""
            ? resolveAmaronDispositionIdsForKanbanColumnSlug(slugForDispositionFilter, args.viewLevel)
            : [];
    let dispositionFilterDispositionIds: Array<Uuid> | undefined;
    if (columnDispositionFilterIds.length > 0) {
        if (toolbarDispositionFilterIds.length === 0) {
            dispositionFilterDispositionIds = columnDispositionFilterIds;
        } else {
            const columnIdSet = new Set(columnDispositionFilterIds.map((id) => String(id)));
            const intersection = toolbarDispositionFilterIds.filter((id) =>
                columnIdSet.has(String(id)),
            );
            dispositionFilterDispositionIds =
                intersection.length > 0 ? intersection : columnDispositionFilterIds;
        }
    } else if (toolbarDispositionFilterIds.length > 0) {
        dispositionFilterDispositionIds = toolbarDispositionFilterIds;
    }

    const mappedSortConfiguration = args.mapSortConfigurationToFields(args.sortConfiguration);

    return {
        workspaceId: args.workspaceId,
        viewLevel: args.viewLevel,
        offset: args.offset,
        limit: args.limit,
        filters: convertFilterConfigurationToSrimanFilters(
            backendFilterConfiguration,
            args.searchConfiguration,
        ),
        orderBy: convertSortConfigurationToSrimanOrderBy(mappedSortConfiguration),
        notificationFilter: args.notificationFilter,
        kanbanColumnSlug: args.kanbanColumnSlug ?? null,
        dispositionFilterDispositionIds:
            dispositionFilterDispositionIds !== undefined && dispositionFilterDispositionIds.length > 0
                ? dispositionFilterDispositionIds
                : undefined,
        clientFilterLineItems: buildAmaronDispositionTrackerClientFilterLineItems(args.filterConfiguration),
        sortLineItems: mappedSortConfiguration.lineItems.map((lineItem) => ({
            columnId: lineItem.columnId,
            direction: lineItem.direction,
        })),
        facetColumnId: args.facetColumnId,
        includeRows: args.includeRows,
        includeTotalCount: args.includeTotalCount,
        includeKanbanColumnCounts: args.includeKanbanColumnCounts,
    };
}

export async function queryAmaronDispositionTracker(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AmaronDispositionTrackerQueryRequest;
}): Promise<Result<AmaronDispositionTrackerQueryResult>> {
    if (
        args.request.clientFilterLineItems !== undefined &&
        args.request.clientFilterLineItems.some(
            (lineItem) =>
                lineItem.operation === FilterOperation.in &&
                (lineItem.values === undefined || lineItem.values.length === 0),
        ) === true
    ) {
        return {
            success: true,
            data: {
                rows: [],
                totalCount: 0,
                kanbanColumnCounts: {},
                columnValues: args.request.facetColumnId !== undefined ? [] : undefined,
            },
        };
    }

    const responseResult = await robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AMARON_DISPOSITION_TRACKER_QUERY_API_PATH}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${args.accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": args.request.workspaceId,
            },
            body: JSON.stringify(args.request),
        },
        amaronDispositionTrackerQueryResponseSchema,
    );

    if (responseResult.success !== true) {
        return responseResult;
    }

    return {
        success: true,
        data: {
            rows: responseResult.data.rows.map(mapApiEventContainerRowToKanbanEventContainer),
            totalCount: responseResult.data.totalCount,
            kanbanColumnCounts: responseResult.data.kanbanColumnCounts,
            columnValues:
                responseResult.data.columnValues === null || responseResult.data.columnValues === undefined
                    ? undefined
                    : normalizeColumnValuesFromApi(responseResult.data.columnValues),
        },
    };
}

export async function queryAgentViewDispositionTracker(args: {
    intellsysBaseUrl: string;
    accessToken: string;
    request: AgentViewDispositionTrackerQueryRequest;
}): Promise<Result<AmaronDispositionTrackerQueryResult>> {
    const responseResult = await robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${AGENT_VIEW_DISPOSITION_TRACKER_QUERY_API_PATH}`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${args.accessToken}`,
                "Content-Type": "application/json",
                "X-Workspace-Id": args.request.workspaceId,
            },
            body: JSON.stringify({
                includeKanbanColumnCounts: args.request.includeKanbanColumnCounts,
                includeRows: args.request.includeRows,
                includeTotalCount: args.request.includeTotalCount,
                kanbanColumnSlug: args.request.kanbanColumnSlug,
                limit: args.request.limit,
                offset: args.request.offset,
                viewLevel: args.request.viewLevel,
            }),
        },
        amaronDispositionTrackerQueryResponseSchema,
    );

    if (responseResult.success !== true) {
        return responseResult;
    }

    return {
        success: true,
        data: {
            rows: responseResult.data.rows.map(mapApiEventContainerRowToKanbanEventContainer),
            totalCount: responseResult.data.totalCount,
            kanbanColumnCounts: responseResult.data.kanbanColumnCounts,
            columnValues:
                responseResult.data.columnValues === null || responseResult.data.columnValues === undefined
                    ? undefined
                    : normalizeColumnValuesFromApi(responseResult.data.columnValues),
        },
    };
}
